// Diagnose API client — POST /ingest/diagnose.
//
// 19BO+ Line 2 B4.3 · client-side counterpart to packages/ingest/src/diagnose/
// + packages/ingest/src/api/endpoints/diagnose.ts. Sibling to evaluate.ts —
// same discipline (postV4 + error mapping), different verdict shape.
//
// Why this uses postV4 (same rationale as evaluate.ts):
//   - postV4 injects the CSRF header (X-Requested-With: fetch) the backend's
//     csrf-header middleware requires on POST.
//   - postV4 validates the response against DiagnoseResponseSchema and reports
//     api-error telemetry on HTTP failure (19BL Session 3c single chokepoint).
//   - Keeps diagnose consistent with the other v4 endpoints.
//
// Error codes surfaced here mirror the backend (endpoints/diagnose.ts):
//   insufficient_credits(402) · schema_fail(422, retryable)
//   stats_empty(400 · diagnose 独有 · stats.total===0 时后端拒绝)
//   provider_error(502) · no_provider(503) · fixture_mode(503)
//   validation_failed(400) · csrf_header_missing(403)

import { DiagnoseResponseSchema } from '@examforge/shared/api';
import type { DiagnoseRequest, DiagnoseResponse } from '@examforge/shared/api';
import { postV4, IngestApiError } from '../lib/api-client.js';

/**
 * Call POST /ingest/diagnose. Throws IngestApiError on HTTP failure
 * (with `.response.error` = the backend code), IngestContractError on a
 * shape mismatch (FE/BE drift — should not happen).
 *
 * Body shape = DiagnoseRequest, produced by web/src/lib/stats.ts wrongAnswerStats()
 * (already 1:1 aligned with DiagnoseRequestSchema).
 */
export async function postDiagnose(req: DiagnoseRequest): Promise<DiagnoseResponse> {
  return postV4('/ingest/diagnose', req, DiagnoseResponseSchema);
}

/**
 * Map a thrown value from a diagnose call to a user-facing message. Pure +
 * total: never throws (the caller is a catch block).
 *
 * Branches mirror the backend's documented error codes (diagnose.ts).
 * Differs from evaluate's mapping by:
 *   - 诊断失败 instead of 评分失败 (domain voice)
 *   - stats_empty (diagnose-only code): no wrong answers to diagnose yet
 *   - no item_invalid (diagnose grades aggregate stats, not a single item)
 */
export function mapDiagnoseErrorToMessage(err: unknown): string {
  if (err instanceof IngestApiError) {
    switch (err.response.error) {
      case 'insufficient_credits':
        return '点数余额不足，请充值后再诊断';
      case 'schema_fail':
        return '诊断结果格式异常，请稍后重试';
      case 'stats_empty':
        return '还没有错题数据可以诊断，先做题积累错题后再来';
      case 'invalid_json':
        return '请求数据格式错误，请刷新页面后重试';
      case 'provider_error':
        return `诊断失败：${err.response.message ?? 'LLM 调用出错'}`;
      case 'no_provider':
      case 'fixture_mode':
        return '当前部署未启用真实 LLM，无法诊断（联系 operator）';
      case 'csrf_header_missing':
        return '请求被安全校验拦截，请刷新页面后重试';
      case 'validation_failed':
        return '请求参数校验失败，请检查错题数据后重试';
      case 'router_init_failed':
      case 'response_assembly_failed':
        return `诊断服务异常：${err.response.error}（请联系 operator）`;
      default:
        return `诊断失败：${err.response.message ?? err.response.error}`;
    }
  }
  if (err instanceof Error) {
    return err.message ?? '诊断失败：未知错误';
  }
  return '诊断失败：未知错误';
}
